export enum WalletStatus {
  ACTIVE = "ACTIVE",
  SUSPENDED = "SUSPENDED",
  CLOSED = "CLOSED",
}

export enum UserType {
  CUSTOMER = "CUSTOMER",
  SELLER = "SELLER",
}

export enum WalletComponent {
  REFERRAL_COMMISSION = "REFERRAL_COMMISSION",
  SPONSOR_COMMISSION = "SPONSOR_COMMISSION",
  FLOW_COMMISSION = "FLOW_COMMISSION",
  UNITY_COMMISSION = "UNITY_COMMISSION",
  COMMUNITY_GROWTH_COMMISSION = "COMMUNITY_GROWTH_COMMISSION",
  INFINITY_COMMISSION = "INFINITY_COMMISSION",
  MILESTONE_COMMISSION = "MILESTONE_COMMISSION",
  PEARLS_COMMISSION = "PEARLS_COMMISSION",
  SALAR_COINS = "SALAR_COINS",
  SHOPPING_VOUCHER = "SHOPPING_VOUCHER",
  SALAR_GIFT_CREDITS = "SALAR_GIFT_CREDITS",
  ADVERTISEMENT_CREDITS = "ADVERTISEMENT_CREDITS",
  FUNDS = "FUNDS",
}

export enum TransactionType {
  CREDIT = "CREDIT",
  DEBIT = "DEBIT",
}

export enum TransactionStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  REVERSED = "REVERSED",
}

export enum TransactionSource {
  PAYMENT = "PAYMENT",
  COMMISSION = "COMMISSION",
  REFUND = "REFUND",
  TRANSFER = "TRANSFER",
  WITHDRAWAL = "WITHDRAWAL",
  ADMIN = "ADMIN",
  BONUS = "BONUS",
  REWARD = "REWARD",
  DELIVERY = "DELIVERY",
  ADVERTISEMENT = "ADVERTISEMENT",
}

export interface WalletBalanceResponse {
  success: boolean;
  data: {
    walletId?: string;
    walletNumber?: string;
    userId: string;
    userType?: UserType;
    availableBalance?: number;
    pendingBalance?: number;
    lockedBalance?: number;
    commissionBalances?: {
      referralCommission: number;
      sponsorCommission: number;
      flowCommission: number;
      unityCommission: number;
      communityGrowthCommission: number;
      samriddhiCommission: number;
      milestoneReferralCommission: number;
      pearlsCommission: number;
      partnerGrowthReferral: number;
      totalCommissions: number;
    };
    paymentBalances?: {
      salarCoins: number;
      shoppingVoucher: number;
      salarGiftCredits: number;
    };
    funds?: number;
    currency?: string;
    status?: WalletStatus;
    stats?: {
      totalEarned: number;
      totalWithdrawn: number;
      totalSpent: number;
      totalTransferredOut: number;
      totalTransferredIn: number;
    };
    registerId?: string;
    totalAvailable?: number;
    components?: Array<{
      component: string;
      displayName: string;
      balance: number;
      isEligible: boolean;
    }>;
    shoppingVoucher?: number;
  };
}

export interface WalletBalanceSummaryResponse {
  success: boolean;
  data: {
    walletId: string;
    walletNumber: string;
    availableBalance: number;
    pendingBalance: number;
    currency: string;
    status: WalletStatus;
  };
}

export interface ComponentBalanceResponse {
  success: boolean;
  data: {
    component: WalletComponent;
    balance: number;
  };
}

export interface WalletTransactionCounterparty {
  userId: string;
  registerId?: string;
  name?: string;
  transferNumber?: string;
}

export interface WalletTransaction {
  id: string;
  transactionNumber: string;
  type: TransactionType;
  component: WalletComponent;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  source: TransactionSource;
  sourceService?: string;
  referenceId?: string;
  description?: string;
  status: TransactionStatus;
  createdAt: string;
  deductions?: {
    grossAmount: number;
    adminFeePercentage: number;
    adminFeeAmount: number;
    tdsPercentage: number;
    tdsAmount: number;
    netAmount: number;
  };
  ledgerId?: string;
  purpose?: string;
  referenceType?: string;
  orderNumber?: string;
  orderDate?: string;
  purchaseType?: string;
  productType?: string;
  customerName?: string;
  customerRegisterId?: string;
  customerReferrerName?: string;
  sellerName?: string;
  failureReason?: string;
  metadata?: Record<string, unknown>;
  counterparty?: WalletTransactionCounterparty;
}

export interface WalletTransactionListResponse {
  success: boolean;
  data: {
    transactions: WalletTransaction[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface ExportTransactionsResponse {
  success: boolean;
  data: {
    content: string;
    filename: string;
  };
}

export interface WalletTransactionQuery {
  page?: number;
  limit?: number;
  type?: TransactionType;
  component?: WalletComponent;
  source?: TransactionSource;
  status?: TransactionStatus;
  startDate?: string;
  endDate?: string;
  sortOrder?: "ASC" | "DESC";
  userType?: UserType;
}

export interface WalletState {
  balance: WalletBalanceResponse["data"] | null;
  summary: WalletBalanceSummaryResponse["data"] | null;
  transactions: WalletTransaction[];
  pagination: WalletTransactionListResponse["data"]["pagination"] | null;
  loading: boolean;
  error: string | null;
  exportData: ExportTransactionsResponse["data"] | null;
}
